// Cozy cart drawer. Implements Dawn's cart contract (getSectionsToRender / renderContents /
// setActiveElement) under the <cart-drawer> tag, so Dawn's product-form.js works with it.
// Our own components dispatch a cancelable `cozy:cart-add` event instead.

// Shared with cozy-header.js: close any cozy <dialog> on backdrop click or [data-drawer-close].
if (!window.cozyDialogs) {
  window.cozyDialogs = true;
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-drawer-close]');
    let dialog = closer ? closer.closest('dialog') : null;
    // A click whose target is the <dialog> itself is a backdrop click only if it lands outside the
    // dialog's box (taps on the box's own padding or on content that is mid-update must not close it).
    if (!dialog && e.target instanceof HTMLDialogElement) {
      const r = e.target.getBoundingClientRect();
      const outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      if (outside && (e.clientX || e.clientY)) dialog = e.target;
    }
    if (dialog?.open) dialog.close();
  });
}

// Free-shipping bar (snippets/cozy-free-shipping.liquid), used by the drawer and the cart page.
// Every cart change re-renders the bar, so each new <cozy-ship> takes over from the one it replaced:
// - the warmth slides from the old amount to the new one (CSS transitions on transform/opacity);
// - crossing the goal plays the "shipping is on us" moment once per visit, never on a plain re-render;
// - a changed message waits in CozyShip.take(key) for the host to add it to its live announcement.
// Reduced motion (OS setting or the accessibility menu): no slide, no celebration, same information.
if (!customElements.get('cozy-ship')) {
  const last = {}; // per bar key: the state of the bar that was just replaced
  const pending = {}; // per bar key: { text, at } for the host's next announcement
  const calm = () => (window.cozyCalm ? window.cozyCalm() : matchMedia('(prefers-reduced-motion: reduce)').matches);
  let cheered = false;
  const cheerOnce = () => {
    try {
      if (sessionStorage.getItem('cozy-ship-cheered')) return false;
      sessionStorage.setItem('cozy-ship-cheered', '1');
    } catch (e) {
      // Storage blocked: once per page view instead.
    }
    if (cheered) return false;
    cheered = true;
    return true;
  };

  window.CozyShip = {
    // The bar's new message if it changed in the last render (for the host's live region), else ''.
    take(key) {
      const item = pending[key];
      delete pending[key];
      return item && Date.now() - item.at < 3000 ? item.text : '';
    },
  };

  customElements.define(
    'cozy-ship',
    class CozyShip extends HTMLElement {
      connectedCallback() {
        const key = this.dataset.shipKey;
        const prev = last[key];
        delete last[key];
        if (!prev) return; // first render on this page: shown as is
        const p = this.style.getPropertyValue('--p').trim();
        const { state, say } = this.dataset;
        this.final = { p, state, say };

        if (say !== prev.say) pending[key] = { text: say, at: Date.now() };
        if (calm()) return;

        if (say !== prev.say) this.classList.add('is-fresh');
        if (p !== prev.p || state !== prev.state) {
          // A new element has no "before" style to transition from: start it at the old amount and
          // state, then let the transitions carry it to the new ones.
          this.style.setProperty('--p', prev.p);
          this.dataset.state = prev.state;
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              this.style.setProperty('--p', p);
              this.dataset.state = state;
            })
          );
        }
        // Goal just crossed (or crossed in a render that was replaced before its moment played).
        if (state === 'reached' && (prev.state !== 'reached' || prev.cheer)) this.armCheer();
      }

      // After the ember has reached the house, and only once the bar is really on screen: an open
      // drawer, or the cart page scrolled to it. A hidden or scrolled-away copy never uses up the moment.
      armCheer() {
        this.cheerPending = true;
        this.cheerTimer = setTimeout(() => {
          this.cheerIO = new IntersectionObserver(
            ([entry]) => {
              if (!entry.isIntersecting) return;
              this.cheerIO.disconnect();
              this.cheerIO = null;
              this.cheerPending = false;
              if (calm() || !cheerOnce()) return;
              this.classList.add('is-celebrating');
              this.cheerTimer = setTimeout(() => this.classList.remove('is-celebrating'), 1600);
            },
            { threshold: 0.6 }
          );
          this.cheerIO.observe(this);
        }, 700);
      }

      disconnectedCallback() {
        clearTimeout(this.cheerTimer);
        this.cheerIO?.disconnect();
        last[this.dataset.shipKey] = {
          ...(this.final || {
            p: this.style.getPropertyValue('--p').trim(),
            state: this.dataset.state,
            say: this.dataset.say,
          }),
          cheer: !!this.cheerPending,
        };
      }
    }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CozyCartDrawer extends HTMLElement {
      static SECTIONS = ['cozy-cart-drawer', 'cart-icon-bubble'];

      connectedCallback() {
        this.dialog = this.querySelector('[data-cart-dialog]');
        this.status = this.querySelector('[data-cart-status]');
        this.strings = window.cozyCartStrings || {};

        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);

        this.onOpenClick = (e) => {
          const trigger = e.target.closest('[data-cart-open]');
          if (!trigger || e.metaKey || e.ctrlKey) return;
          if (window.location.pathname === new URL(trigger.href, location.href).pathname) return; // already on /cart
          e.preventDefault();
          this.open();
        };
        document.addEventListener('click', this.onOpenClick);

        this.onCartAdd = (e) => {
          e.preventDefault(); // tells the sender a drawer handled it (no redirect to /cart)
          // On the cart page the page itself is the cart: keep the drawer shut and let it refresh.
          if (document.querySelector('cozy-cart-page')) {
            if (e.detail?.sections) this.render(e.detail.sections);
            document.dispatchEvent(new CustomEvent('cozy:cart-changed'));
            return;
          }
          e.detail?.sections ? this.renderContents(e.detail) : this.refresh().then(() => (this.bumpCartIcon(), this.open()));
          this.announce(this.strings.added);
        };
        document.addEventListener('cozy:cart-add', this.onCartAdd);

        // Dawn's cart page publishes updates; keep the drawer in sync.
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source !== 'cart-drawer' && event.source !== 'product-form') this.refresh();
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOpenClick);
        document.removeEventListener('cozy:cart-add', this.onCartAdd);
        this.unsubscribe?.();
      }

      /* ---------- Dawn contract ---------- */

      getSectionsToRender() {
        return CozyCartDrawer.SECTIONS.map((id) => ({ id }));
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      renderContents(response) {
        this.render(response.sections);
        this.bumpCartIcon();
        this.open();
      }

      // Add-to-cart feedback: the header bag gives a small bounce as the drawer slides in.
      bumpCartIcon() {
        const icon = document.getElementById('cart-icon-bubble');
        if (!icon) return;
        icon.classList.remove('is-bumped');
        void icon.offsetWidth; // restart the animation
        icon.classList.add('is-bumped');
      }

      /* ---------- Open / close ---------- */

      open() {
        if (this.dialog.open) return;
        this.classList.add('is-entering');
        clearTimeout(this.enterTimer);
        this.enterTimer = setTimeout(() => this.classList.remove('is-entering'), 900);
        this.dialog.showModal();
        document.querySelectorAll('[data-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
        this.dialog.addEventListener(
          'close',
          () => document.querySelectorAll('[data-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'false')),
          { once: true }
        );
      }

      /* ---------- Events ---------- */

      onClick = (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (step) {
          const line = step.closest('[data-line-key]');
          const input = line.querySelector('[data-qty-input]');
          const qty = Math.max(0, Number(input.value) + Number(step.dataset.qtyStep));
          input.value = qty; // show the tap at once; the server render confirms it
          return this.update(line.dataset.lineKey, qty);
        }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) return this.update(remove.closest('[data-line-key]').dataset.lineKey, 0);
        const shipAdd = e.target.closest('[data-ship-add]');
        if (shipAdd) this.addShipItem(shipAdd);
      };

      onChange = (e) => {
        if (!e.target.matches('[data-qty-input]')) return;
        const qty = Math.max(0, parseInt(e.target.value, 10) || 0);
        this.update(e.target.closest('[data-line-key]').dataset.lineKey, qty);
      };

      onSubmit = (e) => {
        // Checkout tapped while a quantity change is still saving: go once it has landed.
        if (e.target.matches('.cozy-cart__checkout-form') && (this.busy || this.queued || this.queuedAdd)) {
          e.preventDefault();
          this.checkoutAfter = true;
          return;
        }
        if (!e.target.matches('[data-upsell-form]')) return;
        e.preventDefault();
        this.addUpsell(e.target);
      };

      /* ---------- Cart API ---------- */

      async update(key, quantity) {
        // A tap during a running update is kept (latest wins) and sent right after it.
        if (this.busy) {
          this.queued = { key, quantity };
          return;
        }
        await this.request(`${window.routes.cart_change_url}.js`, {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: CozyCartDrawer.SECTIONS,
            sections_url: window.location.pathname,
          }),
        });
      }

      async addUpsell(form) {
        const button = form.querySelector('[type="submit"]');
        button.classList.add('is-loading');
        const body = new FormData(form);
        body.append('sections', CozyCartDrawer.SECTIONS.join(','));
        body.append('sections_url', window.location.pathname);
        await this.request(`${window.routes.cart_add_url}.js`, { body }, this.strings.added);
        button.classList.remove('is-loading');
      }

      // Free-shipping suggestion: the chosen closing variant (select) or the only one (button).
      // A tap while another change is saving is kept and sent right after it, like quantity taps.
      async addShipItem(button) {
        const id = button.closest('[data-ship-item]')?.querySelector('[data-ship-variant]')?.value || button.dataset.variantId;
        if (!id) return;
        button.classList.add('is-loading');
        if (this.busy) {
          this.queuedAdd = id;
          return;
        }
        await this.addShipVariant(id);
        button.classList.remove('is-loading');
      }

      async addShipVariant(id) {
        // The tapped card is replaced by the new render: focus goes to the bar, which says what changed
        // (from the tap itself, not document.activeElement: iOS Safari doesn't focus tapped buttons).
        this.focusShip = true;
        const body = new FormData();
        body.append('id', id);
        body.append('quantity', '1');
        body.append('sections', CozyCartDrawer.SECTIONS.join(','));
        body.append('sections_url', window.location.pathname);
        await this.request(`${window.routes.cart_add_url}.js`, { body }, this.strings.added);
        this.focusShip = false;
      }

      async request(url, { headers = {}, body }, successMessage = this.strings.updated) {
        if (this.busy) return;
        this.setBusy(true);
        try {
          const res = await fetch(url, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...headers },
            body,
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || this.strings.error);

          this.render(data.sections);
          this.announce(successMessage);
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
          }
        } catch (err) {
          this.showError(err.message || this.strings.error);
        } finally {
          this.setBusy(false);
          this.afterRequest();
        }
      }

      afterRequest() {
        if (this.queued) {
          const { key, quantity } = this.queued;
          this.queued = null;
          this.update(key, quantity);
          return;
        }
        if (this.queuedAdd) {
          const id = this.queuedAdd;
          this.queuedAdd = null;
          this.addShipVariant(id);
          return;
        }
        if (this.checkoutAfter) {
          this.checkoutAfter = false;
          // Not when the fresh render blocks checkout (a kit item is no longer available).
          const form = this.querySelector('.cozy-cart__checkout-form');
          const checkout = form?.querySelector('[name="checkout"]');
          if (checkout && !checkout.disabled) form.requestSubmit(checkout); // keeps name="checkout": straight to checkout
        }
      }

      async refresh() {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}?sections=${CozyCartDrawer.SECTIONS.join(',')}`);
        if (res.ok) this.render(await res.json());
      }

      /* ---------- Rendering ---------- */

      render(sections) {
        if (!sections) return;
        const active = document.activeElement;
        const hadFocus = this.dialog.open && active && active !== document.body && this.contains(active);
        const lines = [...this.querySelectorAll('[data-line-key]')];
        const lineIndex = lines.indexOf(active?.closest('[data-line-key]'));

        const drawerHtml = sections['cozy-cart-drawer'];
        if (drawerHtml) {
          const next = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('[data-cart-content]');
          this.querySelector('[data-cart-content]').replaceWith(next);
        }

        const bubbleHtml = sections['cart-icon-bubble'];
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubbleHtml && bubble) {
          const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          bubble.innerHTML = (doc.querySelector('.shopify-section') || doc.body).innerHTML;
        }

        // Keep keyboard focus on the same control (ids are stable per line); if its line is gone,
        // on the line that took its place; else on the close button. Never drop it to <body>.
        if (this.focusShip) {
          this.focusShip = false;
          this.querySelector('[data-ship]')?.focus({ preventScroll: true });
        } else if (hadFocus) {
          const nextLines = this.querySelectorAll('[data-line-key]');
          const fallback =
            lineIndex >= 0 && nextLines.length
              ? nextLines[Math.min(lineIndex, nextLines.length - 1)].querySelector('[data-line-remove]')
              : null;
          // A disabled control (e.g. "−" at 1) can't take focus: use that line's quantity field.
          const same = active.id ? document.getElementById(active.id) : null; // no id: not a string ('').closest
          const sameOk = same && !same.disabled ? same : same?.closest('[data-line-key]')?.querySelector('[data-qty-input]');
          const fromShip = active.closest('[data-ship-suggest]') && this.querySelector('[data-ship]');
          (sameOk || fromShip || fallback || this.querySelector('[data-drawer-close]'))?.focus();
        }
      }

      setBusy(busy) {
        this.busy = busy;
        const content = this.querySelector('[data-cart-content]');
        content?.classList.toggle('is-busy', busy);
        content?.setAttribute('aria-busy', String(busy));
      }

      showError(message) {
        const el = this.querySelector('[data-cart-error]');
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
      }

      // One message per change: what happened, then the free-shipping news if it changed.
      announce(message) {
        const text = [message, window.CozyShip?.take('drawer')].filter(Boolean).join('. ');
        if (!text || !this.status) return;
        this.status.textContent = '';
        setTimeout(() => (this.status.textContent = text), 50);
      }
    }
  );
}
